import { ResponseMESF } from "@dexteel/mesf-core";
import {
  ColGroupDef,
  ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-enterprise";
import { ManualAutoButton } from "../../../../../../../shared/data-entries/ManualAutoButton";
import { Torch } from "../../../../models/torches.models";
import { EditAndPLCButtons } from "../../components/EditAndPLCButtons";
import { NumericCellRenderer } from "../NumericCellRenderer";

type Props = {
  editStrand: (torch: Torch) => void;
  readAutomaticStrands: (
    value?: string,
    strandNumber?: number,
  ) => Promise<ResponseMESF>;
  heatStatusCode: string;
  onCellValueChanged: (
    torch: Torch,
    action: "increment" | "decrement",
    field: keyof Torch,
    value: number,
  ) => void;
};

export const useTableData = ({
  editStrand,
  readAutomaticStrands,
  heatStatusCode,
  onCellValueChanged,
}: Props) => {
  const cellStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const columns: ColGroupDef[] = [
    {
      headerName: "Torches",
      children: [
        {
          field: "actions",
          headerName: " ",
          flex: 1,
          cellRenderer: ManualAutoButton,
          cellRendererParams: (params: ICellRendererParams) => ({
            isManual: (params.data as Torch).IsManual,
          }),
          cellStyle: cellStyle,
        },
        {
          field: "Strand",
          headerName: "Strand",
          flex: 2,
          cellStyle: cellStyle,
          maxWidth: 90,
        },
        {
          field: "MoldNameAndSize",
          headerName: "Mold",
          flex: 2,
          cellStyle: cellStyle,
        },
        {
          field: "ColdLength",
          headerName: "Cold Length",
          cellStyle: cellStyle,
          flex: 2,
        },
        {
          field: "HotLength",
          headerName: "Hot Length",
          cellStyle: cellStyle,
          flex: 2,
        },
        {
          field: "WeightRounded",
          colId: "WeightRounded",
          headerName: "Weight Rounded",
          cellStyle: cellStyle,
          flex: 2,
          minWidth: 150,
          valueFormatter: (params) =>
            (params.data as Torch).WeightRounded
              ? (params.data as Torch).WeightRounded?.toFixed(0) || ""
              : "",
        },
        {
          field: "WeightOriginal",
          colId: "WeightOriginal",
          headerName: "Weight Original",
          cellStyle: cellStyle,
          flex: 2,
          valueFormatter: (params) =>
            (params.data as Torch).WeightOriginal
              ? (params.data as Torch).WeightOriginal?.toFixed(2) || ""
              : "",
        },
        {
          field: "TotalCuts",
          colId: "TotalCuts",
          headerName: "#Cuts",
          cellStyle: cellStyle,
          cellRenderer: NumericCellRenderer,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              torch: params.data,
              node: params.node,
              column: params.column,
              api: params.api,
              field: params.column?.getColId() as
                | "TotalCuts"
                | "Undefined"
                | "Scrapped"
                | "Billets",
              onCellValueChanged: onCellValueChanged,
            };
          },
          flex: 2,
        },
        {
          field: "Undefined",
          colId: "Undefined",
          headerName: "#Unidentified",
          cellStyle: cellStyle,
          cellRenderer: NumericCellRenderer,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              torch: params.data,
              node: params.node,
              column: params.column,
              api: params.api,
              field: params.column?.getColId() as
                | "TotalCuts"
                | "Undefined"
                | "Scrapped"
                | "Billets",
              onCellValueChanged: onCellValueChanged,
            };
          },
          flex: 2,
        },
        {
          field: "Scrapped",
          colId: "Scrapped",
          headerName: "#Scrapped",
          cellStyle: cellStyle,
          cellRenderer: NumericCellRenderer,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              torch: params.data,
              node: params.node,
              column: params.column,
              api: params.api,
              field: params.column?.getColId() as
                | "TotalCuts"
                | "Undefined"
                | "Scrapped"
                | "Billets",
              onCellValueChanged: onCellValueChanged,
            };
          },
          flex: 2,
        },
        {
          colId: "Billets",
          valueGetter: (params: ValueGetterParams) => {
            const torch = params.data as Torch;
            return (
              (torch.TotalCuts || 0) -
              (torch.Scrapped || 0) -
              (torch.Undefined || 0)
            );
          },
          headerName: "#Billets",
          cellStyle: cellStyle,
          flex: 2,
        },
        {
          field: "actions",
          headerName: "",
          flex: 1,
          cellRenderer: EditAndPLCButtons,
          cellRendererParams: (params: ICellRendererParams) => ({
            onEdit: editStrand,
            id: params.data as Torch,
            readFromPLC: (value: string) =>
              readAutomaticStrands(value, (params.data as Torch).Strand),
            heatStatusCode: heatStatusCode,
          }),
          cellStyle: cellStyle,
        },
      ],
    },
  ];

  return { columns };
};
